import { Link } from "react-router-dom";
import { BadgeCheck, QrCode, Crown } from "lucide-react";
import { StatusBadge, SimBadge } from "./Badges";
import { RankChange, ProvisionalTag, OfficialProgress, InactivityBadge } from "./Rating";
import { PlayerAvatar } from "./PlayerAvatar";
import { Flag } from "./Flag";
import { qrUrl, SITE_ORIGIN } from "../lib/api";

export function PlayerPassport({ player, club, rating, career, layout = "compact" }) {
  if (!player) return null;
  const r = rating || player.rating;
  const verifyLink = `${SITE_ORIGIN}/verify/player/${player.wsbf_player_id}`;
  const provisional = r && (r.is_provisional || !r.has_rating);
  if (layout === "wide") {
    return <WidePassport player={player} club={club} r={r} career={career} provisional={provisional} verifyLink={verifyLink} />;
  }
  return (
    <div
      data-testid="player-passport-card"
      className="relative overflow-hidden rounded-2xl p-6 sm:p-7 foil-border bg-gradient-to-b from-[#161616] to-[#070707]"
    >
      <div className="absolute inset-0 mesh-grid opacity-30 pointer-events-none" />
      <div className="relative">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <BadgeCheck className="w-5 h-5 text-[#E5B842]" />
            <span className="font-heading font-extrabold uppercase tracking-widest text-[#E5B842] text-sm">
              WSTF Credential
            </span>
          </div>
          <StatusBadge status={player.status} />
        </div>

        <div className="mt-5 flex items-center gap-4">
          <PlayerAvatar player={player} size={64} testid="passport-avatar" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50 truncate">
                {player.display_name}
              </h3>
              {player.is_simulated && <SimBadge />}
            </div>
            {(player.honours || []).some((h) => /World Champion/i.test(h.title)) && (
              <div data-testid="passport-world-champion-badge"
                className="inline-flex items-center gap-1.5 mt-1 px-2.5 py-1 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider bg-gradient-to-r from-[#3a2f0d] to-[#1a1405] text-[#E5B842] border border-[#E5B842]/50">
                <Crown className="w-3.5 h-3.5" />
                {(player.honours.find((h) => /World Champion/i.test(h.title)) || {}).title}
              </div>
            )}
            <div className="text-slate-400 text-sm flex items-center gap-2 mt-0.5">
              <Flag country={player.country} width={20} />
              <span>{player.country?.name}</span>
              {player.city && <span className="text-slate-500">· {player.city}</span>}
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <Field label="WSTF Player ID">
            <span data-testid="passport-wsbf-id" className="font-mono font-bold tracking-widest text-[#E5B842]">
              {player.wsbf_player_id}
            </span>
          </Field>
          <Field label="Club">
            {club?.name || player.club?.name ? (
              <span className="text-slate-100">{club?.name || player.club?.name}</span>
            ) : (
              <span className="text-slate-500 italic">Free agent</span>
            )}
          </Field>
          <Field label="Ranking">
            {r?.has_rating && !provisional && r.world_rank ? (
              <span data-testid="passport-rank" className="inline-flex items-center gap-2">
                <span className="font-heading font-bold text-slate-100">World #{r.world_rank.position}</span>
                <RankChange change={r.world_rank.change} testid="passport-rank-change" />
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <ProvisionalTag matches={r?.matches_count} threshold={r?.official_progress?.results_required} />
                <InactivityBadge active={r?.active_ranking} />
              </span>
            )}
            <OfficialProgress progress={r?.official_progress} status={r?.status} testid="passport-official-progress" />
          </Field>
          <Field label="Rating">
            {r?.has_rating ? (
              <span data-testid="passport-rating" className="font-heading font-black text-lg text-[#E5B842] tabular-nums">
                {Math.round(r.rating)}
                <span className="text-[10px] font-mono text-slate-500 ml-1">±{Math.round(r.rd)}</span>
              </span>
            ) : (
              <span className="text-slate-500 italic">Unrated — provisional</span>
            )}
          </Field>
          {r?.has_rating && !provisional && r.national_rank && (
            <Field label="Greece Rank">
              <span data-testid="passport-national-rank" className="inline-flex items-center gap-2">
                <span className="font-heading font-bold text-slate-100">#{r.national_rank.position}</span>
                <RankChange change={r.national_rank.change} />
              </span>
            </Field>
          )}
          {(career || player.career) && (player.career || career).duels_played > 0 && (
            <Field label="Career Duels">
              <span data-testid="passport-career" className="text-slate-200 font-mono">
                {(career || player.career).duels_won}-{(career || player.career).duels_lost}-{(career || player.career).duels_split}
                <span className="text-slate-500 ml-1">· {(career || player.career).team_points_generated} TP</span>
              </span>
            </Field>
          )}
          {(career || player.career)?.national_team_caps > 0 && (
            <Field label="National Team">
              <span data-testid="passport-nt-caps" className="inline-flex items-center gap-2 text-slate-100">
                {((career || player.career).national_team_memberships || []).map((m) => (
                  <Flag key={m.id} country={m} width={22} className="inline-block" />
                ))}
                <span className="font-heading font-bold text-[#E5B842]">{(career || player.career).national_team_caps}</span>
                <span className="text-[10px] font-mono uppercase text-slate-500">caps</span>
              </span>
            </Field>
          )}
        </div>

        <div className="mt-6 pt-5 border-t border-[#2b2512] flex items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-1 flex items-center gap-1">
              <QrCode className="w-3 h-3" /> Scan to verify
            </div>
            <Link
              to={`/verify/player/${player.wsbf_player_id}`}
              className="text-xs text-slate-400 hover:text-[#E5B842] font-mono break-all"
              style={{ transition: "color 0.15s ease" }}
            >
              /verify/player/{player.wsbf_player_id}
            </Link>
          </div>
          <img
            data-testid="passport-qr"
            src={qrUrl(verifyLink)}
            alt="verification QR"
            className="w-20 h-20 rounded-lg bg-white p-1 shrink-0"
          />
        </div>
        <div className="mt-4 h-1.5 w-full rounded-full" style={{ background: "linear-gradient(90deg,#9B781E,#F3D279,#9B781E)" }} />
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-0.5">
        {label}
      </div>
      <div className="min-w-0 break-words">{children}</div>
    </div>
  );
}

// Big horizontal "official credential" layout used on the public Player Passport page.
// Identity | Performance ranks | QR verify. Same fields/testids as the compact card.
function WidePassport({ player, club, r, career, provisional, verifyLink }) {
  const c = career || player.career;
  const champion = (player.honours || []).find((h) => /World Champion/i.test(h.title));
  const clubName = club?.name || player.club?.name;
  return (
    <div
      data-testid="player-passport-card"
      className="relative overflow-hidden rounded-2xl foil-border bg-gradient-to-b from-[#161616] to-[#070707]"
    >
      <div className="absolute inset-0 mesh-grid opacity-[0.22] pointer-events-none" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(90% 120% at 90% 0%, rgba(212,175,55,0.12) 0%, transparent 55%)" }}
      />
      <div className="relative p-6 sm:p-8">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            <BadgeCheck className="w-5 h-5 text-[#E5B842]" />
            <span className="font-heading font-extrabold uppercase tracking-[0.25em] text-[#E5B842] text-sm">
              WSTF Player Passport
            </span>
          </div>
          <StatusBadge status={player.status} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1.3fr)_auto] gap-8 lg:gap-6 lg:items-center">
          {/* ---- Identity ---- */}
          <div className="flex items-start gap-5 lg:border-r lg:border-[#2b2512] lg:pr-6">
            <PlayerAvatar player={player} size={88} className="sm:!w-24 sm:!h-24 ring-2" testid="passport-avatar-wide" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-heading text-2xl sm:text-4xl font-black uppercase tracking-tight text-slate-50 leading-none wsbf-slant-block break-words">
                  {player.display_name}
                </h2>
                {player.is_simulated && <SimBadge />}
              </div>
              {champion && (
                <div data-testid="passport-world-champion-badge"
                  className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider bg-gradient-to-r from-[#3a2f0d] to-[#1a1405] text-[#E5B842] border border-[#E5B842]/50">
                  <Crown className="w-3.5 h-3.5" /> {champion.title}
                </div>
              )}
              <div className="text-slate-400 text-sm flex items-center gap-2 mt-2">
                <Flag country={player.country} width={22} />
                <span className="text-slate-200">{player.country?.name}</span>
                {player.city && <span className="text-slate-500">· {player.city}</span>}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-x-5 gap-y-2.5">
                <Field label="WSTF Player ID">
                  <span data-testid="passport-wsbf-id" className="font-mono font-bold tracking-widest text-[#E5B842]">
                    {player.wsbf_player_id}
                  </span>
                </Field>
                <Field label="Club">
                  {clubName ? (
                    <span className="text-slate-100">{clubName}</span>
                  ) : (
                    <span className="text-slate-500 italic">Free agent</span>
                  )}
                </Field>
                {player.position && (
                  <Field label="Position">
                    <span className="text-slate-200 font-mono text-xs uppercase tracking-wider">{player.position}</span>
                  </Field>
                )}
                {c && c.duels_played > 0 && (
                  <Field label="Career Duels">
                    <span data-testid="passport-career" className="text-slate-200 font-mono">
                      {c.duels_won}-{c.duels_lost}-{c.duels_split}
                      <span className="text-slate-500 ml-1">· {c.team_points_generated} TP</span>
                    </span>
                  </Field>
                )}
                {c?.national_team_caps > 0 && (
                  <Field label="National Team">
                    <span data-testid="passport-nt-caps" className="inline-flex items-center gap-2 text-slate-100">
                      {(c.national_team_memberships || []).map((m) => (
                        <Flag key={m.id} country={m} width={22} className="inline-block" />
                      ))}
                      <span className="font-heading font-bold text-[#E5B842]">{c.national_team_caps}</span>
                      <span className="text-[10px] font-mono uppercase text-slate-500">caps</span>
                    </span>
                  </Field>
                )}
              </div>
            </div>
          </div>

          {/* ---- Performance: ranks prominent, rating dominant ---- */}
          <div className="grid grid-cols-3 gap-3 lg:pr-6 lg:border-r lg:border-[#2b2512]">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">Rating</div>
              {r?.has_rating ? (
                <div data-testid="passport-rating" className="font-heading text-4xl font-black text-[#E5B842] tabular-nums leading-none">
                  {Math.round(r.rating)}
                  <span className="text-[10px] font-mono text-slate-500 ml-1 align-top">±{Math.round(r.rd)}</span>
                </div>
              ) : (
                <div className="font-heading text-lg font-bold text-slate-500 leading-tight">Unrated</div>
              )}
              <div className="text-[9px] font-mono uppercase tracking-wider text-slate-600 mt-1">ArenaBook</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">World Rank</div>
              {r?.has_rating && !provisional && r.world_rank ? (
                <div data-testid="passport-rank" className="font-heading text-4xl font-black text-slate-50 tabular-nums leading-none flex items-baseline gap-1">
                  #{r.world_rank.position}
                  <RankChange change={r.world_rank.change} testid="passport-rank-change" />
                </div>
              ) : (
                <div className="font-heading text-lg font-bold text-slate-600 leading-tight">—</div>
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">{player.country?.name || "National"} Rank</div>
              {r?.has_rating && !provisional && r.national_rank ? (
                <div data-testid="passport-national-rank" className="font-heading text-4xl font-black text-slate-50 tabular-nums leading-none flex items-baseline gap-1">
                  #{r.national_rank.position}
                  <RankChange change={r.national_rank.change} />
                </div>
              ) : (
                <div className="font-heading text-lg font-bold text-slate-600 leading-tight">—</div>
              )}
            </div>
            <div className="col-span-3 mt-1 flex items-center gap-2 flex-wrap">
              {provisional && <ProvisionalTag matches={r?.matches_count} threshold={r?.official_progress?.results_required} />}
              <InactivityBadge active={r?.active_ranking} />
              <OfficialProgress progress={r?.official_progress} status={r?.status} testid="passport-official-progress" />
            </div>
          </div>

          {/* ---- QR verify ---- */}
          <div className="flex lg:flex-col items-center lg:items-end gap-4 lg:text-right">
            <img
              data-testid="passport-qr"
              src={qrUrl(verifyLink)}
              alt="verification QR"
              className="w-24 h-24 rounded-lg bg-white p-1.5 shrink-0"
            />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-1 flex items-center gap-1 lg:justify-end">
                <QrCode className="w-3 h-3" /> Scan to verify
              </div>
              <Link
                to={`/verify/player/${player.wsbf_player_id}`}
                className="text-xs text-slate-400 hover:text-[#E5B842] font-mono break-all"
                style={{ transition: "color 0.15s ease" }}
              >
                /verify/player/{player.wsbf_player_id}
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-6 h-1.5 w-full rounded-full" style={{ background: "linear-gradient(90deg,#9B781E,#F3D279,#9B781E)" }} />
      </div>
    </div>
  );
}
